/**
 * MobileDaireChat Component - Mobile-optimized version of DaireChat
 * 
 * Mobile-optimized AI-powered chat interface with comprehensive features:
 * - Real-time streaming chat with multiple AI models
 * - Dynamic document suggestions and contextual document analysis
 * - Smart source filtering with IFIA brand colors
 * - Microsoft Speech SDK integration for conversation mode
 * - Rich media support with PDF, News, Events, and Working Group modals
 * - Session persistence with automatic context saving/loading
 * - Mobile-first responsive design with touch-friendly interactions
 * - Swipe gestures and mobile-optimized controls
 */
import React, { useState, useRef, useEffect, useLayoutEffect, forwardRef, useImperativeHandle, useMemo, useCallback, memo, useContext } from 'react';
import './MobileDaireChat.css';
import apiHelper from '../../utils/apiHelper';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { saveChat, loadChats, loadChat, saveSessionContext, getSessionContext, clearSessionContext } from '../../utils/chatStorage';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExpand, faCompress, faHouse, faSave, faFolderOpen, faEraser, faTrashAlt, 
  faInfoCircle, faStop, faSearch, faQuestion, faBook, faFileAlt, faChartLine, 
  faNewspaper, faGlobe, faCog, faExclamationCircle, faCheckCircle, faTimesCircle, 
  faQuestionCircle, faKey, faTimes, faLightbulb, faMicrophone, faMicrophoneSlash, 
  faChevronUp, faChevronDown, faCalendarAlt, faUsers, faClock, faMousePointer, 
  faFilePdf, faBars, faArrowDown, faArrowUp, faEllipsisV,
  faVolumeUp, faVolumeMute, faComments
} from '@fortawesome/free-solid-svg-icons';
import PDFModal from '../../modals/PDFModal';
import NewsModal from '../../modals/NewsModal';
import { toast, ToastContainer } from 'react-toastify';
import { SpeechConfig, SpeechSynthesizer, SpeechRecognizer, ResultReason, AudioConfig } from 'microsoft-cognitiveservices-speech-sdk';
import EventsModal from '../../modals/EventsModal';
import InfoModal from '../../modals/InfoModal';
import { AuthContext } from '../../components/Auth/AuthContext';

function formatWorkingGroupOverview(text) {
  if (!text) return '';
  const summaryIndex = text.indexOf('[Summary]');
  let main = summaryIndex !== -1 ? text.slice(summaryIndex) : text;
  
  main = main.replace(/\[Summary\]/g, '<strong>Summary:</strong><br>');
  main = main.replace(/\[Terms of Reference\]/g, '<strong>Terms of Reference:</strong><br>');
  main = main.replace(/(Objectives:)/g, '<strong>$1</strong><br>');
  main = main.replace(/(Updates:)/g, '<strong>$1</strong><br>');
  main = main.replace(/\n/g, '<br>');
  
  return main;
}

function stripMarkdownForTTS(text) {
  if (!text) return '';
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/#+\s?([^\n]+)/g, '$1')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/\!\[(.*?)\]\((.*?)\)/g, '')
    .replace(/\n{2,}/g, '. ')
    .replace(/\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const MobileDaireChat = forwardRef(function MobileDaireChat({ 
    documentContext, 
    onNavigateHome,
    onRelatedQuestionsUpdate, 
    onUpdatingSummaryChange, 
    onShowHelp, 
    onTopicsUpdate, 
    onMessagesUpdate, 
    onUpdatingTopicsChange,
    onWelcomeShown,
    showDocumentSuggestions: externalShowDocumentSuggestions,
    fullscreen = false
}, ref) {
  const { user: [user] } = useContext(AuthContext);
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const { FILE_NAMES } = require('../../utils/constants');
  
  // Core chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [abortController, setAbortController] = useState(null);
  
  // Error handling state
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [retryCount, setRetryCount] = useState(0);
  const [criticalError, setCriticalError] = useState(null);
  
  // Mobile-specific UI state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileSources, setShowMobileSources] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(fullscreen);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  // Chat functionality state
  const [savedChats, setSavedChats] = useState([]);
  const [showChatModal, setShowChatModal] = useState(false);
  const [hasSavedChats, setHasSavedChats] = useState(false);
  const [lastSearchResults, setLastSearchResults] = useState(null);
  const [selectedModel, setSelectedModel] = useState('gpt-4.1');
  const [maxDocs, setMaxDocs] = useState(50);
  const [truncatedContentFirst, setTruncatedContentFirst] = useState(false);
  
  // Conversation context
  const [conversationContext, setConversationContext] = useState({
    lastIntent: null,
    lastTopic: null,
    topics: [],
    summary: '',
    relatedQuestions: []
  });
  
  // Streaming state
  const [streamingState, setStreamingState] = useState({
    isSearching: false,
    documentsFound: 0,
    isAnalyzing: false,
    searchKeywords: []
  });
  
  // Token usage
  const [tokenUsage, setTokenUsage] = useState({
    systemMessage: 0,
    userMessage: 0,
    documents: 0,
    total: 0
  });
  const [currentMessageTokens, setCurrentMessageTokens] = useState(null);
  const [sessionTotalTokens, setSessionTotalTokens] = useState(0);
  
  // Document and source management
  const [currentDocuments, setCurrentDocuments] = useState(null);
  const [allDocumentSources, setAllDocumentSources] = useState([]);
  const [selectedSourceType, setSelectedSourceType] = useState('all');
  const [visibleSources, setVisibleSources] = useState(3); // Fewer for mobile
  
  // UI interaction state
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [clickedQuestions, setClickedQuestions] = useState(new Set());
  const [isUpdatingSummary, setIsUpdatingSummary] = useState(false);
  const [messageRatings, setMessageRatings] = useState({});
  const [usedDocumentActions, setUsedDocumentActions] = useState(new Set());
  const [showDocumentSuggestions, setShowDocumentSuggestions] = useState(true);
  
  // Speech and conversation mode
  const [isConversationMode, setIsConversationMode] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSynthesis, setSpeechSynthesis] = useState(null);
  const [speechRecognition, setSpeechRecognition] = useState(null);
  const [conversationModeEnabled, setConversationModeEnabled] = useState(false);
  const [speechQueue, setSpeechQueue] = useState([]);
  const speechQueueRef = useRef([]);
  const isSpeakingRef = useRef(false);
  const ttsBufferRef = useRef('');
  const activeSynthRef = useRef(null);
  
  // Modal state
  const [modals, setModals] = useState({});
  const [activeNewsId, setActiveNewsId] = useState(null);
  const [activeNewsType, setActiveNewsType] = useState('news');
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [activeWorkingGroup, setActiveWorkingGroup] = useState(null);
  const [showWorkingGroupModal, setShowWorkingGroupModal] = useState(false);
  const [previewModal, setPreviewModal] = useState({
    show: false,
    data: null,
    type: 'workingGroup'
  });
  
  // Data state
  const [daireSuggestionsData, setDaireSuggestionsData] = useState([]);
  const [daireOptionsData, setDaireOptionsData] = useState(null);
  const [shouldShowWelcome, setShouldShowWelcome] = useState(true);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  
  // Session and context
  const [sessionContext, setSessionContext] = useState({
    lastSearchEmbedding: null,
    lastSearchTimestamp: null
  });
  
  // Disclaimer modals
  const [showFullDisclaimer, setShowFullDisclaimer] = useState(false);
  const [showDisclaimer2Modal, setShowDisclaimer2Modal] = useState(false);
  const [showDaireInfoModal, setShowDaireInfoModal] = useState(false);
  
  // Mobile-specific refs
  const bottomRef = useRef(null);
  const userWasAtBottomRef = useRef(true);
  
  // Error handling and network monitoring
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setError(null);
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      setError({ 
        type: 'network', 
        message: 'You appear to be offline. Please check your internet connection.' 
      });
    };

    // Global error handler for unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      setCriticalError({
        type: 'system',
        message: 'An unexpected error occurred. Please refresh the page.'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Initialize speech services
  useEffect(() => {
    if (isConversationMode) {
      setConversationModeEnabled(true);
      
      const initSpeechServices = async () => {
        try {
          const speechConfig = SpeechConfig.fromSubscription(
            process.env.REACT_APP_SPEECH_KEY,
            process.env.REACT_APP_SPEECH_REGION
          );
          speechConfig.speechSynthesisVoiceName = "en-IE-ConnorNeural";
          
          const synthesizer = new SpeechSynthesizer(speechConfig);
          setSpeechSynthesis(synthesizer);

          const recognizer = new SpeechRecognizer(speechConfig);
          setSpeechRecognition(recognizer);
        } catch (error) {
          console.error('Speech services initialization failed:', error);
          toast.error('Failed to initialize speech services');
        }
      };

      initSpeechServices();
    }
  }, [isConversationMode]);

  // Cleanup speech services
  useEffect(() => {
    return () => {
      if (speechSynthesis) {
        speechSynthesis.close();
      }
      if (speechRecognition) {
        speechRecognition.close();
      }
    };
  }, [speechSynthesis, speechRecognition]);

  // Load data on mount
  useEffect(() => {
    const fetchFileInfoAndContent = async () => {
      try {
        const daireSuggestionsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.DAIRE_SUGGESTIONS);
        setDaireSuggestionsData(daireSuggestionsResult.fileContent);

        const daireOptionsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.DAIRE_OPTIONS);
        setDaireOptionsData(daireOptionsResult.fileContent);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchFileInfoAndContent();
  }, []);

  // Initialize chat with welcome message
  useEffect(() => {
    const initializeChat = async () => {
      try {
        const savedContext = await getSessionContext();

        if (messages.length === 0 && shouldShowWelcome) {
          if (savedContext?.messages?.length) {
            setMessages(savedContext.messages);
            setConversationContext(savedContext.conversationContext || {
              lastIntent: null,
              lastTopic: null,
              topics: [],
              summary: '',
              relatedQuestions: []
            });
            setCurrentDocuments(savedContext.currentDocuments || null);
            setLastSearchResults(savedContext.lastSearchResults || null);
          } else {
            const welcomeOption = daireOptionsData?.find(option => option.title === 'Welcome');
            const formattedName = user?.profile?.name ?
              user.profile.name.split(' ')[0].charAt(0).toUpperCase() + user.profile.name.split(' ')[0].slice(1) :
              'there';

            const welcomeMessage = {
              type: 'bot',
              content: documentContext?.isDocumentContext ?
                `Hello ${formattedName}! I'm here to help you understand <strong>"${documentContext.title}"</strong>. What would you like to know about this document?` :
                formatWelcomeMessage(welcomeOption?.value, user),
              isWelcomeMessage: true
            };

            setMessages([welcomeMessage]);
            setShowQuickActions(!documentContext?.isDocumentContext);
            if (onWelcomeShown) {
              onWelcomeShown();
            }
          }
        }
      } catch (error) {
        console.error('Error initializing chat:', error);
        const welcomeOption = daireOptionsData?.find(option => option.title === 'Welcome');
        const formattedName = user?.profile?.name ?
          user.profile.name.split(' ')[0].charAt(0).toUpperCase() + user.profile.name.split(' ')[0].slice(1) :
          'there';

        const welcomeMessage = {
          type: 'bot',
          content: documentContext?.isDocumentContext ?
            `Hello ${formattedName}! I'm here to help you understand <strong>"${documentContext.title}"</strong>. What would you like to know about this document?` :
            formatWelcomeMessage(welcomeOption?.value, user),
          isWelcomeMessage: true
        };

        setMessages([welcomeMessage]);
        setShowQuickActions(!documentContext?.isDocumentContext);
      }
    };

    if (user && daireOptionsData) {
      initializeChat();
    }
  }, [user, documentContext, messages.length, shouldShowWelcome, daireOptionsData, onWelcomeShown]);

  // Mobile keyboard handling
  useEffect(() => {
    const handleResize = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const windowHeight = window.innerHeight;
      const keyboardHeight = windowHeight - viewportHeight;
      setKeyboardHeight(keyboardHeight > 0 ? keyboardHeight : 0);
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport.removeEventListener('resize', handleResize);
    }
  }, []);

  // Hide cookie consent widgets in mobile view
  useEffect(() => {
    const hideCookieElements = () => {
      const cookieSelectors = [
        '#cookiescript_badge',
        '.cookiescript_badge',
        '[id*="cookie"]',
        '[class*="cookie"]',
        '[data-testid*="cookie"]',
        'iframe[src*="cookie"]',
        '[style*="position: fixed"][style*="bottom"]',
        '[style*="position:fixed"][style*="bottom"]'
      ];

      cookieSelectors.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach(element => {
            element.style.display = 'none';
            element.style.visibility = 'hidden';
            element.style.opacity = '0';
            element.style.pointerEvents = 'none';
            element.style.position = 'absolute';
            element.style.left = '-9999px';
            element.style.top = '-9999px';
          });
        } catch (error) {
          // Ignore invalid selectors
        }
      });
    };

    // Initial hide
    hideCookieElements();

    // Set up observer for dynamically added elements
    const observer = new MutationObserver((mutations) => {
      let shouldHide = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldHide = true;
        }
      });
      if (shouldHide) {
        setTimeout(hideCookieElements, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false
    });

    // Also run periodically as fallback
    const interval = setInterval(hideCookieElements, 2000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  // Auto-scroll handling for mobile
  useLayoutEffect(() => {
    if (chatWindowRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatWindowRef.current;
      userWasAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 50;
    }
  }, [messages.length]);

  useEffect(() => {
    if (chatWindowRef.current && userWasAtBottomRef.current) {
      setTimeout(() => {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
      }, 100);
    }
  }, [messages]);

  // Speech synthesis function
  const speakText = (text) => {
    return new Promise((resolve) => {
      if (!isConversationMode) {
        return resolve();
      }
      try {
        const audioConfig = AudioConfig.fromDefaultSpeakerOutput();
        const speechConfig = SpeechConfig.fromSubscription(
          process.env.REACT_APP_SPEECH_KEY,
          process.env.REACT_APP_SPEECH_REGION
        );
        speechConfig.speechSynthesisVoiceName = "en-IE-ConnorNeural";
        const synthesizer = new SpeechSynthesizer(speechConfig, audioConfig);
        activeSynthRef.current = synthesizer;

        synthesizer.speakTextAsync(
          text,
          (result) => {
            synthesizer.close();
            activeSynthRef.current = null;
            resolve();
          },
          (error) => {
            synthesizer.close();
            activeSynthRef.current = null;
            resolve();
          }
        );
      } catch (error) {
        if (activeSynthRef.current) activeSynthRef.current = null;
        resolve();
      }
    });
  };

  // Speech recognition function
  const startListening = async () => {
    if (!speechRecognition || !isConversationMode) return;

    try {
      setIsListening(true);
      const result = await speechRecognition.recognizeOnceAsync();
      if (result.reason === ResultReason.RecognizedSpeech) {
        const recognizedText = result.text;
        setInput(recognizedText);
        handleSubmit(null, recognizedText);
      }
    } catch (error) {
      console.error('Speech recognition error:', error);
    } finally {
      setIsListening(false);
    }
  };

  // Main chat submission handler
  const handleSubmit = async (e, overrideInput) => {
    if (e) e.preventDefault();
    
    // Sanitize input for security
    const rawMessage = overrideInput !== undefined ? overrideInput : input;
    const messageToSend = sanitizeInput(rawMessage);
    
    if (!messageToSend.trim() || isProcessing) return;

    if (isFirstMessage) {
      setIsFirstMessage(false);
    }

    setInput('');
    setShowMobileSources(false);
    setIsMobileMenuOpen(false);

    const newMessages = [...messages, { type: 'user', content: messageToSend }];
    setMessages(newMessages);
    setIsProcessing(true);

    const controller = new AbortController();
    setAbortController(controller);

    try {
      const authToken = await apiHelper.getAuthToken();
      
      if (documentContext?.listItemId || documentContext?.url) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}daire/documentSuggestions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            listItemId: Number(documentContext.listItemId),
            documentName: documentContext.title,
            query: messageToSend,
            userName: user?.profile?.name,
            userEmail: user?.profile?.email,
            url: documentContext.url,
            aiClient: selectedModel,
            truncatedContentFirst: truncatedContentFirst
          }),
          signal: controller.signal
        });
        
        if (!response.ok) throw new Error('Network response was not ok');
        await processStreamingResponse(response);
      } else {
        const isNews = isNewsQuery(messageToSend);
        const requestBody = {
          messages: newMessages.slice(-10).map(msg => ({
            role: msg.type === 'user' ? 'user' : 'assistant',
            content: msg.content
          })),
          userName: user?.profile?.name,
          userEmail: user?.profile?.email,
          documentContext: {
            ...sessionContext,
            userEmail: user?.profile?.email
          },
          typeFilter: isNews ? 'news' : undefined,
          model: selectedModel,
          truncatedContentFirst: truncatedContentFirst,
          maxDocs: maxDocs
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL}daire/daireintent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal
        });
        
        if (!response.ok) throw new Error('Network response was not ok');
        await processStreamingResponse(response);
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setMessages(prev => [...prev, {
          type: 'error',
          content: 'Sorry, there was an error processing your message.'
        }]);
      }
    } finally {
      setIsProcessing(false);
      setAbortController(null);
    }
  };

  // Process streaming response
  const processStreamingResponse = async (response) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let content = '';
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      buffer += chunk;

      let eventEnd;
      while ((eventEnd = buffer.indexOf('\n\n')) !== -1) {
        const event = buffer.slice(0, eventEnd);
        buffer = buffer.slice(eventEnd + 2);
        
        if (event.startsWith('data: ')) {
          const data = event.slice(6);
          if (data.trim() === '[DONE]') {
            if (isConversationMode && ttsBufferRef.current) {
              speakText(stripMarkdownForTTS(ttsBufferRef.current));
              ttsBufferRef.current = '';
            }
            continue;
          }
          
          try {
            const parsed = JSON.parse(data);
            await handleStreamingData(parsed, content);
            
            if (parsed.content) {
              content += parsed.content;
              if (isConversationMode) {
                ttsBufferRef.current += parsed.content;
              }
            }
          } catch (error) {
            console.error('Failed to parse SSE data:', error);
          }
        }
      }
    }
  };

  // Handle streaming data
  const handleStreamingData = async (parsed, content) => {
    switch (parsed.type) {
      case 'keywords':
        setStreamingState(prev => ({
          ...prev,
          searchKeywords: parsed.keywords
        }));
        break;

      case 'topics':
        setConversationContext(prev => ({
          ...prev,
          topics: parsed.topics || []
        }));
        if (onUpdatingTopicsChange) onUpdatingTopicsChange(false);
        break;

      case 'document_count':
        setStreamingState(prev => ({
          ...prev,
          isSearching: false,
          documentsFound: parsed.count,
          isAnalyzing: true
        }));
        break;

      case 'content':
        setMessages(prev => {
          const updated = [...prev];
          const lastMessage = updated[updated.length - 1];
          if (lastMessage?.type === 'bot') {
            lastMessage.content = content + parsed.content;
          } else {
            updated.push({ type: 'bot', content: content + parsed.content });
          }
          return updated;
        });
        break;

      case 'documents':
        setAllDocumentSources(parsed.documents || []);
        setMessages(prev => {
          const updated = [...prev];
          const lastMessage = updated[updated.length - 1];
          if (lastMessage?.type === 'bot') {
            lastMessage.documents = parsed.documents;
          } else {
            updated.push({
              type: 'bot',
              content: content,
              documents: parsed.documents
            });
          }
          return updated;
        });
        break;

      case 'usage_tracked':
        setMessages(prev => {
          const updated = [...prev];
          const lastMessage = updated[updated.length - 1];
          if (lastMessage?.type === 'bot') {
            lastMessage.listId = parsed.listId;
            lastMessage.totalTokens = parsed.totalTokens;
          }
          return updated;
        });
        if (parsed.totalTokens) {
          setCurrentMessageTokens(parsed.totalTokens);
          setSessionTotalTokens(prev => prev + parsed.totalTokens);
        }
        break;

      case 'error':
        setMessages(prev => [...prev, {
          type: 'error',
          content: parsed.error || 'An error occurred while processing your request.'
        }]);
        break;
    }

    if (parsed.type === 'documents' && parsed.tokenUsage) {
      setTokenUsage(parsed.tokenUsage);
    }
  };

  // Quick action handler
  const handleQuickAction = async (query) => {
    if (query === "ETF_INTELLIGENCE_NAVIGATE") {
      window.location.href = '/etfbook?openETFChat=true';
      return;
    }
    await handleSubmit(null, query);
  };

  // Utility functions
  const isNewsQuery = (query) => {
    const newsKeywords = ['news', 'trending', 'latest', 'recent', 'update'];
    const queryLower = query.toLowerCase();
    return newsKeywords.some(keyword => queryLower.includes(keyword));
  };

  const formatWelcomeMessage = (message, user) => {
    if (!message) return '';

    const formattedName = user?.profile?.name ?
      user.profile.name.split(' ')[0].charAt(0).toUpperCase() + user.profile.name.split(' ')[0].slice(1) :
      'there';

    const currentDate = new Date();
    const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
    const currentYear = currentDate.getFullYear();
    const priorMonthDate = new Date(currentDate);
    priorMonthDate.setMonth(currentDate.getMonth() - 1);
    const priorMonthName = priorMonthDate.toLocaleString('default', { month: 'long' });
    
    return message
      .replaceAll('MemberFirstName', formattedName)
      .replaceAll('MemberLastName', user?.profile?.name?.split(' ')[1] || '')
      .replaceAll('MemberFullName', user?.profile?.name || 'there')
      .replaceAll('CurrentMonth', currentMonth)
      .replaceAll('CurrentYear', currentYear)
      .replaceAll('PriorMonth', priorMonthName);
  };

  const getDisclaimer = (disclaimerName) => {
    const disclaimer = daireOptionsData?.find(option => option.title === disclaimerName);
    return disclaimer?.value || '';
  };

  const handleCopy = async (messageId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRating = async (messageId, rating) => {
    try {
      const messageToRate = messages[messageId];
      if (!messageToRate) return;

      const listId = messageToRate.listId || (documentContext?.isDocumentContext ? documentContext.listItemId : null);
      if (!listId) return;

      await apiHelper.updateChatRating(messageId, rating, user?.profile?.email, listId);
      setMessageRatings(prev => ({
        ...prev,
        [messageId]: rating
      }));
      
      toast.success(`Rating ${rating === 'positive' ? '👍' : '👎'} saved!`, {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      toast.error('Failed to save rating');
    }
  };

  const handleClearChat = async () => {
    try {
      setMessages([]);
      setCurrentDocuments(null);
      setClickedQuestions(new Set());
      setConversationContext({
        lastIntent: null,
        lastTopic: null,
        topics: [],
        summary: '',
        relatedQuestions: []
      });
      setShowQuickActions(!documentContext?.isDocumentContext);
      setAllDocumentSources([]);
      setMessageRatings({});
      setUsedDocumentActions(new Set());
      setTokenUsage({
        systemMessage: 0,
        userMessage: 0,
        documents: 0,
        total: 0
      });
      setCurrentMessageTokens(null);
      setSessionTotalTokens(0);
      setShouldShowWelcome(true);
      setIsMobileMenuOpen(false);

      try {
        await clearSessionContext();
      } catch (error) {
        console.error('Error clearing session context:', error);
      }
    } catch (error) {
      console.error('Error in handleClearChat:', error);
    }
  };

  // Save and load chat functions
  const handleSaveChat = async () => {
    try {
      const savedChat = await saveChat(messages);
      await loadSavedChats();
      const chatTitle = messages.find(m => m.type === 'user')?.content.split(' ').slice(0, 5).join(' ') + '...';
      toast.success(`Chat "${chatTitle}" saved successfully!`, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setIsMobileMenuOpen(false);
    } catch (error) {
      toast.error('Failed to save chat');
    }
  };

  const loadSavedChats = async () => {
    try {
      const chats = await loadChats();
      const sortedChats = chats.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setSavedChats(sortedChats);
      setHasSavedChats(sortedChats.length > 0);
    } catch (error) {
      setHasSavedChats(false);
    }
  };

  const handleLoadChat = async (id) => {
    try {
      const chat = await loadChat(id);
      setMessages(chat.messages);
      setShowChatModal(false);
      setIsMobileMenuOpen(false);
      setShowQuickActions(true);
      setUsedDocumentActions(new Set());
      if (documentContext?.isDocumentContext) {
        setShowDocumentSuggestions(false);
      }
    } catch (error) {
      console.error('Error loading chat:', error);
    }
  };

  const handleDeleteChat = async (e, chatId) => {
    e.stopPropagation();
    try {
      const currentChats = await loadChats();
      const updatedChats = currentChats.filter(chat => chat.id !== chatId);
      localStorage.setItem('saved_chats', JSON.stringify(updatedChats));
      setSavedChats(updatedChats);
      setHasSavedChats(updatedChats.length > 0);
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  // Modal handlers
  const openPDFModal = (id, title, fileName) => {
    const maxZIndex = Math.max(...Object.values(modals).map(modal => modal.zIndex || 0), 0) + 1;
    setModals(prev => ({
      ...prev,
      [id]: {
        id,
        isOpen: true,
        title,
        pdfUrl: fileName,
        top: '5%',
        left: '5%',
        zIndex: maxZIndex
      }
    }));
  };

  const closePDFModal = (id) => {
    setModals(prevModals => {
      const newModals = { ...prevModals };
      if (newModals[id]) {
        newModals[id].isOpen = false;
      }
      return newModals;
    });
  };

  const handleNewsShow = (newsItem) => {
    const newsId = newsItem.documentId || newsItem.listItemId || newsItem.id;
    const articleType = newsItem.type === 'Submitted Article' ? 'submitted' : 'news';
    setActiveNewsId(newsId);
    setActiveNewsType(articleType);
    setShowNewsModal(true);
  };

  const handleWorkingGroupShow = (workingGroup) => {
    setPreviewModal({
      show: true,
      data: workingGroup,
      type: 'workingGroup'
    });
  };

  const closePreviewModal = () => {
    setPreviewModal({
      show: false,
      data: null,
      type: 'workingGroup'
    });
  };

  // Source management
  const getSourceTypeInfo = (doc) => {
    if (doc.type === 'Event' || doc.eventDate || doc.EventDate) {
      return { 
        type: 'event', 
        icon: faCalendarAlt, 
        color: '#bd2130',
        bgColor: '#fdf2f2',
        label: 'Event',
        textColor: '#bd2130'
      };
    }
    if (doc.type === 'Working Group') {
      return { 
        type: 'working-group', 
        icon: faUsers, 
        color: '#006d92',
        bgColor: '#f0f8fb',
        label: 'Working Group',
        textColor: '#006d92'
      };
    }
    if (doc.type === 'Submitted Article') {
      return { 
        type: 'submitted-article', 
        icon: faNewspaper, 
        color: '#8b5cf6',
        bgColor: '#f3f4f6',
        label: 'Submitted Article',
        textColor: '#8b5cf6'
      };
    }
    if (doc.type === 'News' || doc.publishDate) {
      return { 
        type: 'news', 
        icon: faNewspaper, 
        color: '#eb7b15',
        bgColor: '#fef7f0',
        label: 'News',
        textColor: '#eb7b15'
      };
    }
    return { 
      type: 'repository', 
      icon: faFileAlt, 
      color: '#56b32f',
      bgColor: '#f3f8f3',
      label: 'Repository',
      textColor: '#385724'
    };
  };

  const sourceCounts = useMemo(() => {
    const counts = {
      all: 0,
      news: 0,
      repository: 0,
      workingGroups: 0,
      events: 0
    };

    allDocumentSources.forEach(doc => {
      if (doc.score >= 15) {
        counts.all++;
        if (doc.type === 'Working Group') {
          counts.workingGroups++;
        } else if (doc.type === 'Event' || doc.eventDate || doc.EventDate) {
          counts.events++;
        } else if (doc.type === 'News' || doc.publishDate || doc.type === 'Submitted Article') {
          counts.news++;
        } else {
          counts.repository++;
        }
      }
    });
    return counts;
  }, [allDocumentSources]);

  const filteredSources = useMemo(() => {
    return allDocumentSources.filter(doc => {
      const meetsScoreThreshold = doc.score >= 15;
      if (!meetsScoreThreshold) return false;
      
      if (selectedSourceType === 'workingGroups') {
        return doc.type === 'Working Group';
      }
      if (selectedSourceType === 'events') {
        return doc.type === 'Event' || doc.eventDate || doc.EventDate;
      }
      if (selectedSourceType === 'news') {
        return doc.type === 'News' || doc.publishDate || doc.type === 'Submitted Article';
      }
      if (selectedSourceType === 'repository') {
        return doc.type !== 'News' && !doc.publishDate && doc.type !== 'Working Group' && doc.type !== 'Event' && !doc.eventDate && !doc.EventDate && doc.type !== 'Submitted Article';
      }
      return true;
    });
  }, [allDocumentSources, selectedSourceType]);

  const stripHtml = (html) => {
    if (!html) return "No content available";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    let text = tmp.textContent || tmp.innerText || "No content available";
    text = text.replace(/&#58;/g, ' ');
    text = text.replace(/\s+/g, ' ');
    text = text.replace(/(\d{1,2}\s+[A-Za-z]+\s+\d{4})([A-Za-z])/g, '$1 $2');
    text = text.replace(/\.([A-Z])/g, '. $1');
    text = text.replace(/(\d{4})([A-Za-z])/g, '$1 $2');
    return text.trim();
  };

  const cleanMetadata = (text) => {
    if (!text) return '';
    
    // Remove "[Summary:" specifically
    let cleanedText = text.replace(/\[Summary:/g, '');
    
    // Find the last occurrence of "]" and return everything after it
    const lastBracketIndex = cleanedText.lastIndexOf(']');
    if (lastBracketIndex === -1) {
      return cleanedText.trim(); // No brackets found, return cleaned text
    }
    return cleanedText.substring(lastBracketIndex + 1).trim();
  };

  const truncateText = (text, maxLength = 120) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Security utilities
  const sanitizeInput = (input) => {
    if (!input || typeof input !== 'string') return '';
    
    // Remove potential XSS threats
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') // Remove iframes
      .replace(/javascript:/gi, '') // Remove javascript: urls
      .replace(/on\w+\s*=/gi, '') // Remove event handlers
      .trim()
      .slice(0, 2000); // Limit length to prevent abuse
  };

  const validateApiResponse = (response) => {
    // Basic response validation
    if (!response || typeof response !== 'object') {
      throw new Error('Invalid API response format');
    }
    
    // Check for required fields based on expected response structure
    return response;
  };

  // Error handling utilities
  const handleApiError = (error, customMessage = null) => {
    // Don't log sensitive information in production
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', error);
    }
    
    let errorMessage = customMessage;
    
    if (!errorMessage) {
      if (error.name === 'AbortError') {
        return; // User cancelled, don't show error
      } else if (error.status === 429) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (error.status >= 500) {
        errorMessage = 'Server error. Please try again in a few moments.';
      } else if (error.status === 401) {
        errorMessage = 'Authentication error. Please refresh the page and try again.';
      } else if (!navigator.onLine) {
        errorMessage = 'You appear to be offline. Please check your connection.';
      } else {
        errorMessage = 'An error occurred. Please try again.';
      }
    }
    
    setError({ type: 'api', message: errorMessage });
    toast.error(errorMessage);
  };

  const retryWithBackoff = async (fn, maxRetries = 3, delay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn();
      } catch (error) {
        if (i === maxRetries - 1) throw error;
        if (error.name === 'AbortError') throw error; // Don't retry aborted requests
        
        const backoffDelay = delay * Math.pow(2, i);
        setRetryCount(i + 1);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
      }
    }
  };

  // Secure API call utility
  const makeSecureApiCall = async (url, options = {}) => {
    // Validate environment variables
    if (!process.env.REACT_APP_API_URL) {
      throw new Error('API URL not configured');
    }

    // Ensure URL is from trusted domain
    const apiUrl = new URL(url, process.env.REACT_APP_API_URL);
    if (!apiUrl.href.startsWith(process.env.REACT_APP_API_URL)) {
      throw new Error('Invalid API endpoint');
    }

    // Add security headers and timeout
    const secureOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest', // CSRF protection
        ...options.headers
      },
      credentials: 'same-origin', // Prevent CSRF
      timeout: 30000, // 30 second timeout
    };

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), secureOptions.timeout);
    
    try {
      const response = await fetch(apiUrl.href, {
        ...secureOptions,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const isPDF = (filename) => {
    if (!filename) return false;
    const extension = filename.split('.').pop()?.toLowerCase();
    return extension === 'pdf';
  };

  // Load saved chats on mount
  useEffect(() => {
    const checkSavedChats = async () => {
      try {
        const chats = await loadChats();
        const sortedChats = chats.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setSavedChats(sortedChats);
        setHasSavedChats(sortedChats.length > 0);
      } catch (error) {
        setHasSavedChats(false);
      }
    };
    checkSavedChats();
  }, [messages]);

  // Save context when state changes
  useEffect(() => {
    const saveContext = async () => {
      try {
        await saveSessionContext({
          messages,
          conversationContext,
          currentDocuments,
          lastSearchResults
        });
      } catch (error) {
        console.error('Error saving session context:', error);
      }
    };

    if (messages.length > 0) {
      saveContext();
    }
  }, [messages, conversationContext, currentDocuments, lastSearchResults]);

  // Notify parent components
  useEffect(() => {
    if (onRelatedQuestionsUpdate) {
      onRelatedQuestionsUpdate(conversationContext.relatedQuestions || []);
    }
  }, [conversationContext.relatedQuestions, onRelatedQuestionsUpdate]);

  useEffect(() => {
    if (onTopicsUpdate) {
      onTopicsUpdate(conversationContext.topics || []);
    }
  }, [conversationContext.topics, onTopicsUpdate]);

  useEffect(() => {
    if (onUpdatingSummaryChange) {
      onUpdatingSummaryChange(isUpdatingSummary);
    }
  }, [isUpdatingSummary, onUpdatingSummaryChange]);

  useEffect(() => {
    if (onMessagesUpdate) {
      onMessagesUpdate(messages);
    }
  }, [messages, onMessagesUpdate]);

  // Components configuration for ReactMarkdown
  // Markdown renderer configuration (memoized for performance)
  const markdownComponents = useMemo(() => ({
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  }), []);

  // Imperative handle for parent component control
  useImperativeHandle(ref, () => ({
    sendQuestionToChat: (question) => {
      setInput(question);
      handleSubmit(null, question);
    },
    scrollToBottom: () => {
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }));

  // Render functions
  const renderMessage = (message, index) => {
    // Hide actions for welcome messages or if it's the first bot message
    const isWelcome = (index === 0 && message.type === 'bot') || 
                      (message.type === 'bot' && message.content && message.content.includes('Welcome'));
    return (
      <div key={index} className={`mobile-message ${message.type}`}>
        {message.type === 'user' ? (
          <div className="mobile-user-message-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
              className="mobile-markdown-content"
            >
              {message.content}
            </ReactMarkdown>
          </div>
        ) : (
          <>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
              className="mobile-markdown-content"
            >
              {message.content}
            </ReactMarkdown>
            {!isWelcome && (
              <div className="mobile-message-actions">
                <button
                  className={`mobile-message-action-button ${copiedMessageId === index ? 'copy-success' : ''}`}
                  onClick={() => handleCopy(index, message.content)}
                >
                  {copiedMessageId === index ? (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-copy"></i>
                      <span>Copy</span>
                    </>
                  )}
                </button>
                {message.type === 'bot' && (
                  <div className="mobile-rating-buttons">
                    <button
                      className={`mobile-rating-button thumbs-up ${messageRatings[index] === 'positive' ? 'active' : ''}`}
                      onClick={() => handleRating(index, 'positive')}
                      title="Thumbs up"
                    >
                      <i className="fas fa-thumbs-up"></i>
                    </button>
                    <button
                      className={`mobile-rating-button thumbs-down ${messageRatings[index] === 'negative' ? 'active' : ''}`}
                      onClick={() => handleRating(index, 'negative')}
                      title="Thumbs down"
                    >
                      <i className="fas fa-thumbs-down"></i>
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderMobileHeader = () => (
    <div className="mobile-header mobile-daire-header">
      <div className="mobile-header-left">
        <button 
          className="mobile-back-btn"
          onClick={() => {
            if (onNavigateHome) {
              onNavigateHome();
            } else {
              window.history.back();
            }
          }}
          title="Home"
          aria-label="Go to home page"
          role="button"
          tabIndex={0}
        >
          <FontAwesomeIcon icon={faHouse} aria-hidden="true" />
        </button>
        {currentMessageTokens && (
          <Tippy content="Tokens used for the last AI response. Tokens are units of text that the AI processes - roughly 4 characters per token.">
            <div className="mobile-token-display">
              <span className="mobile-token-count">{currentMessageTokens.toLocaleString()}</span>
            </div>
          </Tippy>
        )}
        <div className="mobile-daire-title">
                      <img 
              src="/assets/img/daire/daire-logo-icon.png" 
              alt="Daire AI" 
              className={`mobile-daire-icon ${isProcessing ? 'spinning' : ''}`} 
            />
          <span>Daire</span>
        </div>
      </div>
      <div className="mobile-header-right">
        <button 
          className="mobile-clear-button"
          onClick={handleClearChat}
          title="Clear Chat"
          aria-label="Clear all messages"
          role="button"
          tabIndex={0}
        >
          <FontAwesomeIcon icon={faEraser} aria-hidden="true" />
        </button>
        <button 
          className="mobile-menu-button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          title="Menu"
          aria-label="Open menu"
          role="button"
          tabIndex={0}
        >
          <FontAwesomeIcon icon={faBars} aria-hidden="true" />
        </button>
      </div>
    </div>
  );

  const renderMobileMenu = () => (
    <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
      <div className="mobile-menu-content">
        <div className="mobile-menu-header">
          <h3>Chat Options</h3>
          <button 
            className="mobile-menu-close"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="mobile-menu-items">
          {messages.some(msg => msg.type === 'user') && (
            <button 
              className="mobile-menu-item"
              onClick={handleSaveChat}
            >
              <FontAwesomeIcon icon={faSave} />
              <span>Save Chat</span>
            </button>
          )}
          
          {hasSavedChats && (
            <button 
              className="mobile-menu-item"
              onClick={() => {
                setShowChatModal(true);
                setIsMobileMenuOpen(false);
              }}
            >
              <FontAwesomeIcon icon={faFolderOpen} />
              <span>Load Chat</span>
            </button>
          )}
          
          {messages.some(msg => msg.type === 'user') && (
            <button 
              className="mobile-menu-item"
              onClick={handleClearChat}
            >
              <FontAwesomeIcon icon={faEraser} />
              <span>Clear Chat</span>
            </button>
          )}
          
          <button 
            className="mobile-menu-item"
            onClick={() => {
              setShowDaireInfoModal(true);
              setIsMobileMenuOpen(false);
            }}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>About Daire</span>
          </button>
          
          {/* <button 
            className={`mobile-menu-item ${isConversationMode ? 'active' : ''}`}
            onClick={() => setIsConversationMode(!isConversationMode)}
          >
            <FontAwesomeIcon icon={isConversationMode ? faMicrophoneSlash : faMicrophone} />
            <span>{isConversationMode ? 'Disable Voice' : 'Enable Voice'}</span>
          </button> */}
        </div>
      </div>
    </div>
  );

  const renderQuickActions = () => {
    if (messages.length > 1 || !showQuickActions) return null;

    const getIconComponent = (iconName) => {
      if (!iconName) return null;
      const iconMap = {
        'search': faSearch,
        'question': faQuestion,
        'book': faBook,
        'file': faFileAlt,
        'chart': faChartLine,
        'news': faNewspaper,
        'globe': faGlobe,
        'settings': faCog,
        'info': faInfoCircle,
        'warning': faExclamationCircle,
        'success': faCheckCircle,
        'error': faTimesCircle
      };
      const icon = iconMap[iconName];
      return icon ? <FontAwesomeIcon icon={icon} /> : <span className="custom-icon-text">{iconName}</span>;
    };

    const currentDate = new Date();
    const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
    const currentYear = currentDate.getFullYear();
    const priorMonthDate = new Date(currentDate);
    priorMonthDate.setMonth(currentDate.getMonth() - 1);
    const priorMonthName = priorMonthDate.toLocaleString('default', { month: 'long' });

    return (
      <div className="mobile-suggestion-boxes">
        {daireSuggestionsData.slice(0, 3).map((action, index) => {
          const formattedTitle = action.title
            .replaceAll('{Month}', currentMonth)
            .replaceAll('{Year}', currentYear)
            .replaceAll('{PriorMonth}', priorMonthName);
          const formattedQuery = action.query
            .replaceAll('{Month}', currentMonth)
            .replaceAll('{Year}', currentYear)
            .replaceAll('{PriorMonth}', priorMonthName);

          return (
            <button
              key={index}
              className="mobile-suggestion-box"
              onClick={() => handleQuickAction(formattedQuery)}
            >
              <span className="mobile-suggestion-icon">
                {getIconComponent(action.icon)}
              </span>
              <span className="mobile-suggestion-text">{formattedTitle}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderDocumentActions = () => {
    if (!documentContext?.isDocumentContext) return null;

    const documentActions = [
      {
        title: "Key points?",
        icon: <FontAwesomeIcon icon={faKey} size="sm" />,
        query: "What are the key points?"
      },
      {
        title: "Main arguments?",
        icon: <FontAwesomeIcon icon={faQuestionCircle} size="sm" />,
        query: "What are the main arguments being put forward?"
      },
      {
        title: "Summarize",
        icon: <FontAwesomeIcon icon={faFileAlt} size="sm" />,
        query: "Can you provide a summary?"
      },
    ];

    const handleActionClick = (query) => {
      setUsedDocumentActions(prev => new Set([...prev, query]));
      handleQuickAction(query);
    };

    return (
      <div className="mobile-document-suggestions">
        {externalShowDocumentSuggestions && showDocumentSuggestions && (
          <>
            {usedDocumentActions.size < 3 && (
              <button 
                className="mobile-toggle-suggestions"
                onClick={() => setShowDocumentSuggestions(false)}
              >
                <FontAwesomeIcon icon={faTimes} size="sm" />
              </button>
            )}
            <div className="mobile-suggestion-boxes">
              {documentActions.map((action, index) => (
                !usedDocumentActions.has(action.query) && (
                  <button
                    key={index}
                    className="mobile-suggestion-box"
                    onClick={() => handleActionClick(action.query)}
                  >
                    <span className="mobile-suggestion-icon">{action.icon}</span>
                    <span className="mobile-suggestion-text">{action.title}</span>
                  </button>
                )
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderStreamingStatus = () => {
    let statusText = '';
    if (streamingState.isSearching) {
      statusText = 'Searching documents...';
    } else if (streamingState.isAnalyzing) {
      statusText = 'Analyzing content...';
    } else if (isProcessing) {
      statusText = 'Processing...';
    }

    // Only render the entire div when there's actually something to show
    if (!(streamingState.isSearching || streamingState.isAnalyzing || isProcessing)) {
      return null;
    }

    return (
      <div className="mobile-streaming-status">
        <div className="mobile-typing-indicator">
          <div className="mobile-typing-dot"></div>
          <div className="mobile-typing-dot"></div>
          <div className="mobile-typing-dot"></div>
        </div>
        <span className="mobile-status-text">{statusText}</span>
      </div>
    );
  };

  const renderMobileSources = () => {
    if (!showMobileSources || allDocumentSources.length === 0) return null;

    return (
      <div className="mobile-sources-section">
        <div className="mobile-sources-header">
          <div className="mobile-sources-title">
            <FontAwesomeIcon icon={faSearch} />
            <span>Sources ({filteredSources.length})</span>
          </div>
          <button 
            className="mobile-sources-close"
            onClick={() => setShowMobileSources(false)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="mobile-source-filters">
          {['all', 'news', 'events', 'workingGroups', 'repository'].map(type => {
            const getTooltipText = (type) => {
              switch(type) {
                case 'all': return 'All Sources';
                case 'news': return 'News';
                case 'events': return 'Events';
                case 'workingGroups': return 'Working Groups';
                case 'repository': return 'Repository';
                default: return type.charAt(0).toUpperCase() + type.slice(1);
              }
            };

            return (
              <Tippy key={type} content={getTooltipText(type)}>
                <button
                  className={`mobile-source-filter ${selectedSourceType === type ? 'active' : ''}`}
                  onClick={() => setSelectedSourceType(type)}
                >
                  {type === 'all' && <FontAwesomeIcon icon={faGlobe} />}
                  {type === 'news' && <FontAwesomeIcon icon={faNewspaper} />}
                  {type === 'events' && <FontAwesomeIcon icon={faCalendarAlt} />}
                  {type === 'workingGroups' && <FontAwesomeIcon icon={faUsers} />}
                  {type === 'repository' && <FontAwesomeIcon icon={faFileAlt} />}
                  <span className="mobile-filter-count">{sourceCounts[type] || 0}</span>
                </button>
              </Tippy>
            );
          })}
        </div>
        
        <div className="mobile-sources-list">
          {filteredSources.slice(0, visibleSources).map((doc, index) => {
            const typeInfo = getSourceTypeInfo(doc);
            
            return (
              <div
                key={index}
                className={`mobile-source-item ${typeInfo.type}`}
              >
                <div className="mobile-source-header">
                  <div className="mobile-source-badge" style={{ backgroundColor: typeInfo.bgColor, color: typeInfo.textColor }}>
                    <FontAwesomeIcon icon={typeInfo.icon} />
                    <span>{typeInfo.label}</span>
                  </div>
                  <div className="mobile-source-score">
                    {Math.round(doc.score)}%
                  </div>
                </div>
                
                <div className="mobile-source-content">
                  <h4 className="mobile-source-title">
                    {doc.title}
                    {isPDF(doc.fileName) && (
                      <FontAwesomeIcon icon={faFilePdf} className="mobile-pdf-indicator" />
                    )}
                  </h4>
                  
                  {(doc.publishDate || doc.eventDate || doc.EventDate) && (
                    <div className="mobile-source-date">
                      <FontAwesomeIcon icon={faClock} />
                      <span>{new Date(doc.publishDate || doc.eventDate || doc.EventDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}</span>
                    </div>
                  )}
                  
                  <Tippy 
                    content={stripHtml(cleanMetadata(doc.summary || "No summary available"))}
                    maxWidth={300}
                    interactive={true}
                    placement="top"
                  >
                    <div className="mobile-source-description">
                      {truncateText(stripHtml(cleanMetadata(doc.summary || "No summary available")))}
                    </div>
                  </Tippy>
                </div>
              </div>
            );
          })}
          
          {visibleSources < filteredSources.length && (
            <button
              className="mobile-show-more-sources"
              onClick={() => setVisibleSources(v => Math.min(v + 3, filteredSources.length))}
            >
              <FontAwesomeIcon icon={faChevronDown} />
              <span>Show {Math.min(3, filteredSources.length - visibleSources)} more</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderSavedChatsModal = () => {
    if (!showChatModal) return null;

    return (
      <div className="mobile-chat-modal" onClick={() => setShowChatModal(false)}>
        <div className="mobile-chat-modal-content" onClick={e => e.stopPropagation()}>
          <div className="mobile-chat-modal-header">
            <h3>Saved Chats</h3>
            <button 
              className="mobile-chat-modal-close" 
              onClick={() => setShowChatModal(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="mobile-chat-modal-disclaimer">
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>Chats are stored locally in your browser.</span>
          </div>
          
          <div className="mobile-chat-list">
            {savedChats.length > 0 ? (
              savedChats.map((chat, index) => (
                <div key={index} className="mobile-chat-item">
                  <div 
                    className="mobile-chat-item-content" 
                    onClick={() => handleLoadChat(chat.id)}
                  >
                    <div className="mobile-chat-item-title">
                      {chat.title || `Chat ${index + 1}`}
                    </div>
                    <div className="mobile-chat-item-date">
                      {new Date(chat.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    className="mobile-chat-item-delete"
                    onClick={(e) => handleDeleteChat(e, chat.id)}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              ))
            ) : (
              <div className="mobile-no-chats">No saved chats found</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderErrorBanner = () => {
    if (!error && !isOffline) return null;
    
    return (
      <div className="mobile-error-banner">
        <div className="mobile-error-content">
          <FontAwesomeIcon icon={faExclamationCircle} className="mobile-error-icon" />
          <span className="mobile-error-text">
            {isOffline ? 'You are currently offline' : error.message}
          </span>
          {retryCount > 0 && (
            <span className="mobile-retry-count">
              (Retry {retryCount}/3)
            </span>
          )}
          <button 
            className="mobile-error-dismiss"
            onClick={() => {
              setError(null);
              setRetryCount(0);
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>
    );
  };

  const renderCriticalError = () => {
    if (!criticalError) return null;
    
    return (
      <div className="mobile-critical-error-overlay">
        <div className="mobile-critical-error-content">
          <FontAwesomeIcon icon={faExclamationCircle} className="mobile-critical-error-icon" />
          <h3>System Error</h3>
          <p>{criticalError.message}</p>
          <button 
            className="mobile-critical-error-button"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  };

  const renderDisclaimer = () => {
    if (messages.length <= 1 || isProcessing) return null;

    return (
      <div className="mobile-daire-disclaimer">
        <div 
          className="mobile-disclaimer-content"
          onClick={() => setShowFullDisclaimer(!showFullDisclaimer)}
        >
          <button 
            className="mobile-disclaimer-icon"
            onClick={(e) => {
              e.stopPropagation();
              setShowDaireInfoModal(true);
            }}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
          </button>
          <div className="mobile-disclaimer-text">
            <span dangerouslySetInnerHTML={{ __html: getDisclaimer('Disclaimer1') }} />
          </div>
          <div className="mobile-disclaimer-toggle">
            <FontAwesomeIcon icon={showFullDisclaimer ? faChevronUp : faChevronDown} />
          </div>
        </div>
        
        {showFullDisclaimer && (
          <div className="mobile-disclaimer-details">
            <div dangerouslySetInnerHTML={{ __html: getDisclaimer('Disclaimer1.2') }} />
          </div>
        )}
      </div>
    );
  };

  // Calculate dynamic heights
  const disclaimerHeight = messages.length > 1 ? 60 : 0; // Account for disclaimer height
  const chatWindowStyle = {
    paddingBottom: keyboardHeight > 0 ? `${keyboardHeight + 20 + disclaimerHeight}px` : `${80 + disclaimerHeight}px`
  };

  const inputFormStyle = {
    bottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
  };

  const hasChatToSave = messages.length > 0 && messages.some(msg => msg.type === 'user');

  return (
    <div 
      className={`mobile-daire-chat ${isFullscreen ? 'fullscreen' : ''}`}
      role="main"
      aria-label="Daire AI Chat Application"
    >
      <ToastContainer position="top-center" />
      
      {renderErrorBanner()}
      {renderMobileHeader()}
      {renderMobileMenu()}
      
      <div 
        className="mobile-chat-window" 
        ref={chatWindowRef} 
        style={chatWindowStyle}
        role="log"
        aria-label="Chat conversation"
        aria-live="polite"
        aria-relevant="additions"
      >
        {messages.map((message, index) => renderMessage(message, index))}
        <div ref={bottomRef} aria-hidden="true" />
        
        {!isProcessing && !documentContext?.isDocumentContext && showQuickActions && renderQuickActions()}
        {!isProcessing && documentContext?.isDocumentContext && renderDocumentActions()}
        {renderStreamingStatus()}
        {renderMobileSources()}
      </div>
      
      {renderDisclaimer()}
      
      <form 
        onSubmit={handleSubmit} 
        className="mobile-input-form" 
        style={inputFormStyle}
        role="search"
        aria-label="Chat with Daire AI"
      >
        <div className="mobile-input-container">
          <label htmlFor="mobile-chat-input" className="sr-only">
            {documentContext?.isDocumentContext 
              ? `Ask questions about ${documentContext.title}` 
              : "Ask Daire AI a question"
            }
          </label>
          <input
            id="mobile-chat-input"
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (e.target.value && !documentContext?.isDocumentContext) {
                setShowQuickActions(false);
                setShowMobileSources(false);
              }
            }}
            placeholder={documentContext?.isDocumentContext ?
              `Ask about "${documentContext.title}"...` :
              "Ask Daire..."}
            disabled={isProcessing}
            className="mobile-input"
            aria-describedby={isProcessing ? "mobile-processing-status" : undefined}
            aria-required="false"
            autoComplete="off"
            role="textbox"
            aria-multiline="false"
          />
          
          {isConversationMode && (
            <button
              type="button"
              onClick={startListening}
              className={`mobile-voice-button ${isListening ? 'listening' : ''}`}
              disabled={isProcessing || isListening}
            >
              <FontAwesomeIcon icon={isListening ? faMicrophoneSlash : faMicrophone} />
            </button>
          )}
          
          <button
            type="submit"
            disabled={isProcessing || !input.trim()}
            className={`mobile-send-button ${isProcessing ? 'processing' : ''}`}
          >
            {isProcessing ? (
              <div className="mobile-stop-button" onClick={(e) => {
                e.preventDefault();
                if (abortController) {
                  abortController.abort();
                }
                setIsProcessing(false);
              }}>
                <FontAwesomeIcon icon={faStop} />
              </div>
            ) : (
              <FontAwesomeIcon icon={faArrowUp} />
            )}
          </button>
          
          {allDocumentSources.length > 0 && (
            <button
              type="button"
              onClick={() => setShowMobileSources(!showMobileSources)}
              className={`mobile-sources-button ${showMobileSources ? 'active' : ''}`}
              disabled={isProcessing}
            >
              <FontAwesomeIcon icon={faBook} />
              <span className="mobile-sources-count">{filteredSources.length}</span>
            </button>
          )}
        </div>
      </form>
      {renderSavedChatsModal()}
      
      {/* Modals */}
      {Object.values(modals).map((modal) => (
        modal.isOpen && (
          <PDFModal
            key={modal.id}
            title={modal.title}
            url={modal.pdfUrl}
            show={modal.isOpen}
            onClose={(e) => {
              e.stopPropagation();
              closePDFModal(modal.id);
            }}
            allowFullscreen={true}
            allowMove={true}
            showLinkinToolBar={true}
            style={{ top: modal.top, left: modal.left, zIndex: modal.zIndex }}
            listItemId={modal.id}
          />
        )
      ))}
      
      {activeNewsId && (
        <NewsModal
          articleId={activeNewsId}
          articleType={activeNewsType}
          show={showNewsModal}
          onClose={() => {
            setShowNewsModal(false);
            setActiveNewsId(null);
            setActiveNewsType('news');
          }}
          allowFullscreen={true}
        />
      )}
      
      {showEventModal && activeEvent && (
        <EventsModal
          data={activeEvent}
          show={showEventModal}
          onClose={() => setShowEventModal(false)}
          allowFullscreen={true}
        />
      )}
      
      {previewModal.show && (
        <div className="mobile-preview-modal-overlay" onClick={closePreviewModal}>
          <div className="mobile-preview-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-preview-modal-header">
              <h3>{previewModal.data?.title || previewModal.data?.Title}</h3>
              <button className="mobile-preview-modal-close" onClick={closePreviewModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="mobile-preview-modal-body">
              <div className="mobile-preview-badge">
                <FontAwesomeIcon icon={faInfoCircle} />
                Preview Mode
              </div>
              <p>
                {previewModal.type === 'steeringGroup' 
                  ? 'Steering Group details will be available in September 2025.' 
                  : 'Working Group details including Terms of Reference, Meeting Minutes, Team Members, and Reference Documents will be available in September 2025.'
                }
              </p>
            </div>
            <div className="mobile-preview-modal-footer">
              <button className="mobile-btn-secondary" onClick={closePreviewModal}>
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showDaireInfoModal && (
        <InfoModal
          title="Daire Usage Policy"
          data={getDisclaimer('Disclaimer3')}
          show={showDaireInfoModal}
          onClose={() => setShowDaireInfoModal(false)}
          small={true}
        />
      )}
      
      {renderCriticalError()}
    </div>
  );
});

export default memo(MobileDaireChat); 